import { Alert, Progress, Button } from 'antd';
import React, { useEffect, useState } from 'react';
import { LoadingComponentProps } from 'react-loadable';
import { RedoOutlined } from '@ant-design/icons';

import LoadingSpinner from '../components/LoadingSpinner';
import { ParentContainer, ProgressContainer } from './WidgetBlockLoading.style';

const DELAY_UNTIL_RETRY_IN_SECONDS = 10;

const getRetryState = () => {
    const msOfDelay = DELAY_UNTIL_RETRY_IN_SECONDS * 1000;

    return {
        retryFetchAt: Date.now() + msOfDelay,
        msLeftUntilRetry: msOfDelay,
    };
};

const WidgetBlockLoading: React.FC<LoadingComponentProps> = ({ error, retry }) => {
    const [{ retryFetchAt, msLeftUntilRetry, lastRetry }, setState] = useState<ReturnType<typeof getRetryState> & { lastRetry: number }>({
        retryFetchAt: -1,
        msLeftUntilRetry: -1,
        lastRetry: -1,
    });

    const tickAndCheckForRetry = (forceRetry = false) => {
        const currentTime = Date.now();

        setState((prev) => {
            const { retryFetchAt, lastRetry } = prev;

            if (retryFetchAt === -1 && lastRetry !== -1) {
                const secondsPastSinceLastRetry = (currentTime - lastRetry) / 1000;

                if (secondsPastSinceLastRetry > DELAY_UNTIL_RETRY_IN_SECONDS / 2) {
                    return { ...getRetryState(), lastRetry: -1 };
                }

                return prev;
            }

            const timeLeftUntilRetry = currentTime - retryFetchAt;

            if (timeLeftUntilRetry > 0 || forceRetry) {
                retry();

                return { retryFetchAt: -1, msLeftUntilRetry: -1, lastRetry: currentTime };
            }

            return { ...prev, msLeftUntilRetry: Math.abs(timeLeftUntilRetry) };
        });
    };

    useEffect(() => {
        const response = setInterval(() => tickAndCheckForRetry(), 100);

        return () => clearInterval(response);
    }, []);

    useEffect(() => {
        if (error && retryFetchAt === -1 && lastRetry === -1) {
            setState((prev) => ({ ...prev, ...getRetryState() }));
        }
    }, [error, retryFetchAt, lastRetry]);

    const isInErrorState = msLeftUntilRetry >= 0;
    const secondsLeftUntilRetry = msLeftUntilRetry / 1000;
    const secondsLeftUntilRetryRounded = Math.max(Math.floor(secondsLeftUntilRetry), 1);
    const timeLeftInPlainEnglish = `${secondsLeftUntilRetryRounded} second${secondsLeftUntilRetryRounded == 1 ? '' : 's'}`;

    return (
        <ParentContainer>
            {!isInErrorState && <LoadingSpinner spinColour={isInErrorState ? 'red' : 'yellow'} />}
            {isInErrorState && (
                <ProgressContainer>
                    <Alert
                        message={
                            <div>
                                <p>{`Failed to load component. Retrying in ${timeLeftInPlainEnglish}...`}</p>
                            </div>
                        }
                        type='error'
                        style={{
                            position: 'absolute',
                            width: '100%',
                            alignSelf: 'flex-start',
                        }}
                    />
                    <Progress
                        type='circle'
                        percent={((DELAY_UNTIL_RETRY_IN_SECONDS - secondsLeftUntilRetry) / DELAY_UNTIL_RETRY_IN_SECONDS) * 100}
                        format={() => timeLeftInPlainEnglish}
                        strokeColor='red'
                        status='exception'
                        width={150}
                        style={{
                            margin: 'auto',
                            width: 'auto',
                            height: 'auto',
                        }}
                    />
                    <Button
                        icon={<RedoOutlined />}
                        onClick={() => tickAndCheckForRetry(true)}
                        style={{
                            alignSelf: 'flex-end',
                            width: '100%',
                            position: 'absolute',
                        }}
                    >
                        Retry now
                    </Button>
                </ProgressContainer>
            )}
        </ParentContainer>
    );
};

export default WidgetBlockLoading;
